import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  useDeleteUserPreference,
  usePatchUserPreference,
  usePreferenceQuery,
} from '../api/hooks';
import { preferencesKeys } from '../api/query-keys';
import type { GetPreferenceResponse, PreferenceSource } from '../api/types';
import {
  deepMergePreferences,
  mergeUserLayerPatch,
} from '../lib/deep-merge-preferences';

const DEFAULT_DEBOUNCE_MS = 500;

export type UseUserPreferenceOptions = {
  debounceMs?: number;
  enabled?: boolean;
};

export type UseUserPreferenceResult<T extends Record<string, unknown>> = {
  value: T;
  source: PreferenceSource;
  /** Full board-layer snapshot or `null` when no board default exists. */
  boardLayer: T | null;
  /** User-layer override; may be partial (fields the user explicitly set). */
  userLayer: Partial<T> | null;
  isOverridingBoardDefault: boolean;
  setValue: (patch: Partial<T> | ((prev: T) => Partial<T>)) => void;
  reset: () => void;
  flush: () => void;
  isLoading: boolean;
  isError: boolean;
};

export const useUserPreference = <T extends Record<string, unknown>>(
  boardId: number | undefined,
  key: string,
  codeDefault: T,
  options?: UseUserPreferenceOptions,
): UseUserPreferenceResult<T> => {
  const debounceMs = options?.debounceMs ?? DEFAULT_DEBOUNCE_MS;
  const enabled = options?.enabled ?? true;

  const queryClient = useQueryClient();
  const query = usePreferenceQuery<T>(boardId, key, { enabled });
  const patchMutation = usePatchUserPreference<T>();
  const deleteMutation = useDeleteUserPreference<T>();

  const boardLayer = query.data?.board?.data ?? null;
  const userLayer = query.data?.user?.data ?? null;

  const value = useMemo<T>(
    () => deepMergePreferences(codeDefault, boardLayer, userLayer),
    [codeDefault, boardLayer, userLayer],
  );

  const source: PreferenceSource =
    userLayer !== null ? 'user' : boardLayer !== null ? 'board' : 'code';

  const isOverridingBoardDefault = userLayer !== null && boardLayer !== null;

  // Refs holding the freshest known optimistic state for debounce/flush.
  // Holds the next user-layer payload (partial blob, fields the user has
  // explicitly set) until the debounce expires and we PATCH it to the API.
  const pendingPatchRef = useRef<Partial<T> | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const buildNextUser = useCallback(
    (patch: Partial<T> | ((prev: T) => Partial<T>)): Partial<T> => {
      // The user-layer base — pending optimistic value (if any) takes
      // priority, otherwise the existing persisted user layer, otherwise an
      // empty object. CRITICAL: never seed from the merged effective value
      // — doing so bakes board defaults into the user override and freezes
      // the user against future admin changes to the board default.
      const prevUserLayer: Partial<T> =
        pendingPatchRef.current ?? userLayer ?? {};

      // Functional patch form receives the currently EFFECTIVE merged
      // value (what the UI shows), so consumer code like
      // `prev.visibility[id]` reads the right thing for toggling logic.
      const effective = deepMergePreferences(
        codeDefault,
        boardLayer,
        prevUserLayer as T,
      );
      const resolvedPatch =
        typeof patch === 'function' ? patch(effective) : patch;

      // Apply the patch onto the USER layer only (same merge semantics as
      // the read path: objects merge, arrays replace). The result is a
      // pure user-layer blob — possibly partial — ready to be persisted as
      // `user.data` without contaminating it with code/board defaults.
      return mergeUserLayerPatch(prevUserLayer, resolvedPatch);
    },
    [boardLayer, codeDefault, userLayer],
  );

  const sendPatch = useCallback(
    (next: Partial<T>) => {
      if (!boardId) return;
      patchMutation.mutate(
        { boardId, key, data: next },
        {
          // Guard against a race between this response and a newer
          // optimistic update from a subsequent setValue() call:
          //   t=0   setValue1 -> optimistic cache=A. debounce.
          //   t=500 sendPatch1 -> PATCH1 in flight. pending=null.
          //   t=550 setValue2 -> optimistic cache=B (B is built on top
          //                       of A from userLayer in cache).
          //   t=700 PATCH1 response arrives. If we naively setQueryData
          //         here we OVERWRITE B with A. The user sees their
          //         second change "snap back" for a moment.
          // Only commit response when no newer change is pending — the
          // newer mutation's own response will eventually settle it.
          onSuccess: response => {
            if (pendingPatchRef.current !== null) return;
            queryClient.setQueryData<GetPreferenceResponse<T>>(
              preferencesKeys.key(boardId, key),
              response,
            );
          },
        },
      );
      pendingPatchRef.current = null;
    },
    [boardId, key, patchMutation, queryClient],
  );

  const setValue = useCallback(
    (patch: Partial<T> | ((prev: T) => Partial<T>)) => {
      if (!boardId) return;

      const nextUser = buildNextUser(patch);
      pendingPatchRef.current = nextUser;

      // Optimistic cache update so consumers re-render immediately.
      const currentResponse = queryClient.getQueryData<
        GetPreferenceResponse<T>
      >(preferencesKeys.key(boardId, key));

      queryClient.setQueryData<GetPreferenceResponse<T>>(
        preferencesKeys.key(boardId, key),
        {
          key,
          board: currentResponse?.board ?? null,
          user: {
            data: nextUser,
            updatedAt:
              currentResponse?.user?.updatedAt ?? new Date().toISOString(),
          },
        },
      );

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        if (pendingPatchRef.current !== null) {
          sendPatch(pendingPatchRef.current);
        }
      }, debounceMs);
    },
    [boardId, buildNextUser, debounceMs, key, queryClient, sendPatch],
  );

  const flush = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (pendingPatchRef.current !== null) {
      sendPatch(pendingPatchRef.current);
    }
  }, [sendPatch]);

  const reset = useCallback(() => {
    if (!boardId) return;
    if (userLayer === null && pendingPatchRef.current === null) {
      return;
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    pendingPatchRef.current = null;
    deleteMutation.mutate(
      { boardId, key },
      {
        // Same race guard as `sendPatch` — see comment there.
        onSuccess: response => {
          if (pendingPatchRef.current !== null) return;
          queryClient.setQueryData<GetPreferenceResponse<T>>(
            preferencesKeys.key(boardId, key),
            response,
          );
        },
      },
    );
  }, [boardId, deleteMutation, key, queryClient, userLayer]);

  // Flush on unmount.
  useEffect(
    () => () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      if (pendingPatchRef.current !== null && boardId) {
        // Synchronous mutate (best-effort) — fetch fires, request goes out
        // even though component is unmounting. We do not await.
        patchMutation.mutate({
          boardId,
          key,
          data: pendingPatchRef.current,
        });
        pendingPatchRef.current = null;
      }
    },
    // We intentionally do not depend on patchMutation: it is recreated each
    // render but we only need the closure on unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return {
    value,
    source,
    boardLayer,
    userLayer,
    isOverridingBoardDefault,
    setValue,
    reset,
    flush,
    isLoading: query.isLoading,
    isError: query.isError,
  };
};
